import { IdpLogout, isIdpLogoutRoute } from '@containers/IdpLogout/idpLogout';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { LocalizedDatePickerProvider } from '@ovComponents/2-component/localizedDatePicker/localizedDatePicker';
import { AuthProvider } from '@providers/AuthProvider';
import { AnalyticsProvider } from '@providers/analyticsProvider';
import Auth0Provider from '@providers/auth0Provider';
import { ThemeProvider } from '@providers/themeProvider';
import { HelmetProvider } from 'react-helmet-async';
import OvApolloProvider from './providers/ovApolloProvider';
import UserContextProvider from './providers/userContextProvider';
import { Router } from './Router';
import 'quill/dist/quill.snow.css';
import 'quill-table-ui/dist/index.css';
import { CONFIG } from '@config';
import { FeatureFlagsProvider } from '@onevesthq/feature-flags/web';
import { featureFlagClient } from '@utils/flagsmith';
import { BootSpinner } from 'hephaestus/web';
import { UseLocalizationProvider } from './hooks/useLocalization';
import InactivityTimeoutProvider from './providers/inActivityTimeoutProvider';

if (CONFIG.datadog.env) {
  datadogLogs.init({
    clientToken: CONFIG.datadog.logClientToken || '',
    site: 'datadoghq.com',
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['error'],
    sessionSampleRate: 100,
    service: 'onevest-app',
    env: CONFIG.datadog.env,
    version: CONFIG.datadog.ddRumVersion || '',
    beforeSend: (log) => {
      if (/^AbortError\b/i.test(log.error?.stack ?? '')) {
        return false;
      }
      return true;
    },
  });

  datadogRum.init({
    applicationId: CONFIG.datadog.rumAppId || '',
    clientToken: CONFIG.datadog.rumClientToken || '',
    site: 'datadoghq.com',
    service: 'onevest-app',
    version: CONFIG.datadog.ddRumVersion || '',
    env: CONFIG.datadog.env,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    defaultPrivacyLevel: 'mask',
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
  });
}

function App() {
  // IdP-initiated logout runs outside the normal provider tree so auth/user
  // bootstrap never runs on this path (see idpLogout.tsx).
  if (isIdpLogoutRoute()) {
    return (
      <ThemeProvider>
        <IdpLogout />
      </ThemeProvider>
    );
  }

  return (
    <HelmetProvider>
      <FeatureFlagsProvider
        client={featureFlagClient}
        fallback={<BootSpinner />}
      >
        <ThemeProvider>
          <UseLocalizationProvider>
            <OvApolloProvider>
              <AuthProvider>
                <Auth0Provider>
                  <UserContextProvider>
                    <LocalizedDatePickerProvider>
                      <AnalyticsProvider>
                        <InactivityTimeoutProvider>
                          <Router />
                        </InactivityTimeoutProvider>
                      </AnalyticsProvider>
                    </LocalizedDatePickerProvider>
                  </UserContextProvider>
                </Auth0Provider>
              </AuthProvider>
            </OvApolloProvider>
          </UseLocalizationProvider>
        </ThemeProvider>
      </FeatureFlagsProvider>
    </HelmetProvider>
  );
}

export default App;
